import { IconSourceEnum } from "src/app/@core/components/ui/icon/icon.component";
import { TOOLS, TOOLKIT_PROVIDERS, CONNECTOR_PROVIDERS, CONNECTOR_PROVIDER_INFO } from "../constants/tool.constants";
import { MessageItem, MESSAGE_TYPE } from "../models/model";
import { TOOL_STATUS } from "../enums/enum";
import { QueryList } from "@angular/core";
import { CanvasComponent } from "../components/tool-views/canvas/canvas.component";

/**
 * Returns the tool status for a message.
 * - "error" when message.status is "error" or message.response_metadata?.status is "error"
 * - "success" when message.status is "success" (and we didn't already hit error above)
 * - "loading" otherwise
 */
export function getToolStatus(message: MessageItem | undefined | null): TOOL_STATUS {
    if (!message) return TOOL_STATUS.LOADING;
    if (message.status === 'error' || message['response_metadata']?.status === 'error') return TOOL_STATUS.ERROR;
    if (message.status === 'success') return TOOL_STATUS.SUCCESS;
    return TOOL_STATUS.LOADING;
}

export function getToolTitle(name: string): string {
    switch (name) {
        case TOOLS.LIST_FILES:
            return 'List Files';
        case TOOLS.READ_FILE:
            return 'Read File';
        case TOOLS.WRITE_FILE:
            return 'Write File';
        case TOOLS.EDIT_FILE:
            return 'Edit File';
        case TOOLS.WEB_SEARCH:
            return 'Web Search';
        case TOOLS.COMPOSIO_SEARCH_TOOLS:
            return 'Search Tools';
        default:
            if (name.startsWith('COMPOSIO_')) {
                name = name.replace('COMPOSIO_', '');
            }
            const normalizedToolName = name.toLowerCase().split('_');
            return normalizedToolName.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    }
}

export function getToolIcon(name: string): { icon: string; iconSource?: IconSourceEnum } {
    switch (name) {
        case TOOLS.LIST_FILES:
            return { icon: 'file-stack' };
        case TOOLS.READ_FILE:
            return { icon: 'file-text' };
        case TOOLS.WRITE_FILE:
            return { icon: 'file-pen' };
        case TOOLS.EDIT_FILE:
            return { icon: 'file-pen' };
        case TOOLS.WEB_SEARCH:
            return { icon: 'search' };
        case TOOLS.COMPOSIO_SEARCH_TOOLS:
            return { icon: 'search' };
        case TOOLS.VIEW_IMAGE:
            return { icon: 'image', iconSource: IconSourceEnum.LUCIDE };
        default: {
            const normalizedToolName = name.toLowerCase().split('_');
            const matchedProvider = Object.values(TOOLKIT_PROVIDERS).find(
                (provider: string) => normalizedToolName.includes(provider.toLowerCase())
            );

            if (matchedProvider) {
                return {
                    iconSource: IconSourceEnum.SVG,
                    icon: CONNECTOR_PROVIDER_INFO[matchedProvider as CONNECTOR_PROVIDERS].icon
                };
            }

            return { icon: 'wrench' };
        }
    }
}


export function getToolName(message: MessageItem, allMessages?: MessageItem[]): string {
    if (!message) return '';

    if (message.type === MESSAGE_TYPE.TOOL) {
        if (message.name) return message.name;
        if (message.additional_kwargs?.metadata?.toolName) return message.additional_kwargs.metadata.toolName;

        // If no name and it's a tool result, look up parent AI message using tool_call_id
        if (message.tool_call_id && allMessages) {
            const parentMsg = allMessages.find(m =>
                m.type === MESSAGE_TYPE.AI &&
                m.tool_calls?.some((tc: any) => tc.id === message.tool_call_id)
            );

            if (parentMsg && parentMsg.tool_calls) {
                const toolCall = parentMsg.tool_calls.find((tc: any) => tc.id === message.tool_call_id);
                if (toolCall) {
                    return toolCall.function?.name || toolCall.name || '';
                }
            }
        }

        return '';
    }

    if (message.type === MESSAGE_TYPE.AI && message.tool_calls && message.tool_calls.length > 0) {
        // Simple extraction for now
        if (message.tool_calls.length === 1) {
            const tc = message.tool_calls[0];
            return tc.function?.name || tc.name || '';
        }
        return 'Multi Tool Execution';
    }

    return message.name || '';
}

export function getArtifactToolStatus(message: MessageItem, allMessages?: MessageItem[]): TOOL_STATUS {
    if (!message) return TOOL_STATUS.LOADING;

    // If it's a tool result, use shared getToolStatus
    if (message.type === MESSAGE_TYPE.TOOL) {
        return getToolStatus(message);
    }

    // If it's an AI message with tool_calls, check for matching tool result
    if (message.type === MESSAGE_TYPE.AI && message.tool_calls && message.tool_calls.length > 0 && allMessages) {
        const toolCallIds = message.tool_calls.map((tc: any) => tc.id);
        const matchingToolResult = allMessages.find(
            msg => msg.type === MESSAGE_TYPE.TOOL && toolCallIds.includes(msg.tool_call_id)
        );
        if (matchingToolResult) {
            return getToolStatus(matchingToolResult);
        }
        return TOOL_STATUS.LOADING;
    }

    return TOOL_STATUS.LOADING;
}

export function getFileNameFromToolCall(chainItem: MessageItem | undefined, allMessages?: MessageItem[]): string | null {
    if (!chainItem) return null;

    // If this is a tool result, look up the parent AI message to get the tool call arguments
    let payload = chainItem.additional_kwargs || {};

    if (chainItem.type === MESSAGE_TYPE.TOOL && chainItem.tool_call_id && allMessages) {
        const parentMsg = allMessages.find(m =>
            m.type === MESSAGE_TYPE.AI &&
            m.tool_calls?.some((tc: any) => tc.id === chainItem.tool_call_id)
        );

        if (parentMsg && parentMsg.tool_calls) {
            const toolCall = parentMsg.tool_calls.find((tc: any) => tc.id === chainItem.tool_call_id);
            if (toolCall) {
                // Use the tool call's arguments instead
                payload = toolCall;
            }
        }
    }

    const rawArgs = payload?.args ?? payload?.function?.arguments ?? payload?.arguments ?? {};
    let args: any = rawArgs;
    if (typeof rawArgs === 'string') {
        try {
            args = JSON.parse(rawArgs);
        } catch {
            args = {};
        }
    }

    const filePath: unknown =
        args?.file_path ??
        args?.target_file ??
        args?.path ??
        args?.file ??
        args?.filename;

    if (typeof filePath !== 'string' || !filePath.trim()) return null;

    const sanitized = filePath.split(/[?#]/)[0];
    const parts = sanitized.replace(/\\/g, '/').split('/').filter(Boolean);
    return (parts.length ? parts[parts.length - 1] : sanitized) || null;
}

export function getToolStringArg(chainItem: MessageItem | undefined, keys: string[], allMessages?: MessageItem[]): string | null {
    if (!chainItem) return null;

    // If this is a tool result, look up the parent AI message to get the tool call arguments
    let payload = chainItem.additional_kwargs || {};

    if (chainItem.type === MESSAGE_TYPE.TOOL && chainItem.tool_call_id && allMessages) {
        const parentMsg = allMessages.find(m =>
            m.type === MESSAGE_TYPE.AI &&
            m.tool_calls?.some((tc: any) => tc.id === chainItem.tool_call_id)
        );

        if (parentMsg && parentMsg.tool_calls) {
            const toolCall = parentMsg.tool_calls.find((tc: any) => tc.id === chainItem.tool_call_id);
            if (toolCall) {
                // Use the tool call's arguments instead
                payload = toolCall;
            }
        }
    }

    const rawArgs = payload?.args ?? payload?.function?.arguments ?? payload?.arguments ?? {};
    let args: any = rawArgs;
    if (typeof rawArgs === 'string') {
        try {
            args = JSON.parse(rawArgs);
        } catch { args = {}; }
    }

    for (const key of keys) {
        const value = args?.[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
}


export function truncateLabel(value: string, max = 52): string {
    const v = value.trim().replace(/\s+/g, ' ');
    return v.length > max ? `${v.slice(0, max - 1)}…` : v;
}

/**
 * Checks if a message is a summarization message by examining additional_kwargs.lc_source
 * @param message - The message item to check
 * @returns true if the message is from summarization, false otherwise
 */
export function isSummarizationMessage(message: MessageItem): boolean {
    return message?.additional_kwargs?.lc_source === 'summarization';
}

export function getArtifactToolData(toolName: string, chainItem: MessageItem | undefined, status: string, allMessages?: MessageItem[]): { icon: string; name: string; iconSource?: IconSourceEnum } {
    switch (toolName) {
        case TOOLS.WRITE_TODOS:
        case TOOLS.CREATE_TODOS:
        case TOOLS.UPDATE_TODOS:
            return {
                icon: 'list-todo',
                name: status === 'loading' ? 'Planning Todos' : (status === 'success' ? 'Todos Created' : 'Write Todos')
            };

        case TOOLS.CREATE_FILE:
        case TOOLS.WRITE_FILE: {
            const fileName = getFileNameFromToolCall(chainItem, allMessages);
            const isCreate = toolName === TOOLS.CREATE_FILE;
            return {
                icon: 'file-pen',
                name: status === 'loading'
                    ? (fileName ? `${isCreate ? 'Creating' : 'Writing'} ${fileName}` : (isCreate ? 'Creating file' : 'Writing file'))
                    : (status === 'success'
                        ? (fileName ? `${isCreate ? 'Created' : 'Wrote'} ${fileName}` : (isCreate ? 'File created' : 'File written'))
                        : (fileName ? `Failed to ${isCreate ? 'create' : 'write'} ${fileName}` : `${isCreate ? 'Create' : 'Write'} file failed`))
            };
        }

        case TOOLS.STR_REPLACE:
        case TOOLS.SEARCH_REPLACE:
        case TOOLS.EDIT_FILE: {
            const fileName = getFileNameFromToolCall(chainItem, allMessages);
            const verb = toolName === TOOLS.EDIT_FILE ? 'edit' : 'modify';
            return {
                icon: toolName === TOOLS.EDIT_FILE ? 'file-pen' : 'file-search',
                name: status === 'loading'
                    ? (fileName ? `${verb === 'edit' ? 'Editing' : 'Modifying'} ${fileName}` : (verb === 'edit' ? 'Editing file' : 'Modifying file'))
                    : (status === 'success'
                        ? (fileName ? `${verb === 'edit' ? 'Edited' : 'Modified'} ${fileName}` : (verb === 'edit' ? 'File edited' : 'File modified'))
                        : (fileName ? `Failed to ${verb} ${fileName}` : `${verb === 'edit' ? 'Edit' : 'Modify'} file failed`))
            };
        }

        case TOOLS.FULL_FILE_REWRITE:
        case TOOLS.REWRITE_FILE:
            {
                const fileName = getFileNameFromToolCall(chainItem, allMessages);
                return {
                    icon: 'file-plus',
                    name: status === 'loading'
                        ? (fileName ? `Rewriting ${fileName}` : 'Rewriting file')
                        : (status === 'success'
                            ? (fileName ? `Rewrote ${fileName}` : 'File rewritten')
                            : (fileName ? `Failed to rewrite ${fileName}` : 'Rewrite file failed'))
                };
            }

        case TOOLS.READ_FILE:
            {
                const readFileName = getFileNameFromToolCall(chainItem, allMessages);
                return {
                    icon: 'file-text',
                    name: status === 'loading'
                        ? (readFileName ? `Reading ${readFileName}` : 'Reading file')
                        : (status === 'success'
                            ? (readFileName ? `Read ${readFileName}` : 'File read')
                            : (readFileName ? `Failed to read ${readFileName}` : 'Read file failed'))
                };
            }

        case TOOLS.DELETE_FILE:
        case TOOLS.REMOVE_FILE:
            {
                const fileName = getFileNameFromToolCall(chainItem, allMessages);
                return {
                    icon: 'file-x',
                    name: status === 'loading'
                        ? (fileName ? `Deleting ${fileName}` : 'Deleting file')
                        : (status === 'success'
                            ? (fileName ? `Deleted ${fileName}` : 'File deleted')
                            : (fileName ? `Failed to delete ${fileName}` : 'Delete file failed'))
                };
            }

        case TOOLS.EXECUTE_COMMAND:
        case TOOLS.RUN_COMMAND:
            {
                const cmd = getToolStringArg(chainItem, ['command', 'cmd', 'shell_command'], allMessages);
                const shortCmd = cmd ? truncateLabel(cmd, 52) : null;
                return {
                    icon: 'terminal',
                    name: status === 'loading'
                        ? (shortCmd ? `Running ${shortCmd}` : 'Running command')
                        : (status === 'success'
                            ? (shortCmd ? `Ran ${shortCmd}` : 'Command executed')
                            : (shortCmd ? `Failed to run ${shortCmd}` : 'Command failed'))
                };
            }

        case TOOLS.WEB_SEARCH:
            {
                const query = getToolStringArg(chainItem, ['query', 'q', 'search_term'], allMessages);
                const shortQuery = query ? truncateLabel(query, 30) : null;
                return {
                    icon: 'globe',
                    name: status === 'loading'
                        ? (shortQuery ? `Searching for "${shortQuery}"` : 'Searching web')
                        : (status === 'success'
                            ? (shortQuery ? `Searched for "${shortQuery}"` : 'Web search completed')
                            : (shortQuery ? `Failed to search "${shortQuery}"` : 'Web search failed'))
                };
            }

        case TOOLS.CRAWL_WEBPAGE:
        case TOOLS.CRAWL_WEB:
        case TOOLS.SCRAPE_WEBPAGE:
        case TOOLS.SCRAPE_WEB:
            {
                const url = getToolStringArg(chainItem, ['url', 'page_url', 'link'], allMessages);
                const shortUrl = url ? truncateLabel(url, 52) : null;
                return {
                    icon: 'globe',
                    name: status === 'loading'
                        ? (shortUrl ? `Fetching: ${shortUrl}` : 'Fetching webpage')
                        : (status === 'success'
                            ? (shortUrl ? `Fetched: ${shortUrl}` : 'Webpage fetched')
                            : (shortUrl ? `Fetch failed: ${shortUrl}` : 'Fetch failed'))
                };
            }

        case TOOLS.CALL_DATA_PROVIDER:
            {
                const provider = getToolStringArg(chainItem, ['provider', 'toolkit', 'service'], allMessages);
                const target = provider ? truncateLabel(provider, 36) : null;
                return {
                    icon: 'external-link',
                    name: status === 'loading'
                        ? (target ? `Calling ${target}` : 'Calling data provider')
                        : (status === 'success'
                            ? (target ? `Called ${target}` : 'Data provider called')
                            : (target ? `Failed calling ${target}` : 'Data provider call failed'))
                };
            }

        case TOOLS.GET_DATA_PROVIDER_ENDPOINTS:
            {
                const provider = getToolStringArg(chainItem, ['provider', 'toolkit', 'service'], allMessages);
                const target = provider ? truncateLabel(provider, 36) : null;
                return {
                    icon: 'network',
                    name: status === 'loading'
                        ? (target ? `Getting endpoints: ${target}` : 'Getting endpoints')
                        : (status === 'success'
                            ? (target ? `Got endpoints: ${target}` : 'Endpoints fetched')
                            : (target ? `Failed getting endpoints: ${target}` : 'Get endpoints failed'))
                };
            }

        case TOOLS.EXECUTE_DATA_PROVIDER_CALL:
            {
                const provider = getToolStringArg(chainItem, ['provider', 'toolkit', 'service'], allMessages);
                const endpoint = getToolStringArg(chainItem, ['endpoint', 'path', 'operation'], allMessages);
                const detail = truncateLabel([provider, endpoint].filter(Boolean).join(' · '), 52);
                return {
                    icon: 'network',
                    name: status === 'loading'
                        ? (detail ? `Running: ${detail}` : 'Running provider call')
                        : (status === 'success'
                            ? (detail ? `Ran: ${detail}` : 'Provider call ran')
                            : (detail ? `Failed running: ${detail}` : 'Provider call failed'))
                };
            }

        case TOOLS.DEPLOY_SITE:
        case TOOLS.DEPLOY:
            return {
                icon: 'layout-template',
                name: status === 'loading' ? 'Deploying site' : (status === 'success' ? 'Site deployed' : 'Deploy failed')
            };

        case TOOLS.EXECUTE_CODE:
        case TOOLS.RUN_CODE:
            return {
                icon: 'terminal',
                name: status === 'loading' ? 'Running code' : (status === 'success' ? 'Code ran' : 'Code run failed')
            };

        case TOOLS.ASK:
        case TOOLS.ASK_USER:
            {
                const question = getToolStringArg(chainItem, ['question', 'prompt', 'text', 'message'], allMessages);
                const shortQ = question ? truncateLabel(question, 52) : null;
                return {
                    icon: 'message-square',
                    name: status === 'loading'
                        ? (shortQ ? `Asking: ${shortQ}` : 'Asking you')
                        : (status === 'success'
                            ? (shortQ ? `Asked: ${shortQ}` : 'Asked you')
                            : (shortQ ? `Ask failed: ${shortQ}` : 'Ask failed'))
                };
            }

        case TOOLS.LIST_FILES:
            {
                const dir = getToolStringArg(chainItem, ['path', 'dir', 'directory'], allMessages);
                const shortDir = dir ? truncateLabel(dir, 52) : null;
                return {
                    icon: 'file-stack',
                    name: status === 'loading'
                        ? (shortDir ? `Listing: ${shortDir}` : 'Listing files')
                        : (status === 'success'
                            ? (shortDir ? `Listed: ${shortDir}` : 'Files listed')
                            : (shortDir ? `List failed: ${shortDir}` : 'List failed'))
                };
            }

        case TOOLS.GREP:
        case TOOLS.GLOB:
            {
                const pattern = getToolStringArg(chainItem, ['pattern', 'glob_pattern', 'glob', 'query', 'regexp', 'regex'], allMessages);
                const where = getToolStringArg(chainItem, ['path', 'target_directory', 'target_directories', 'dir'], allMessages);
                const detail = truncateLabel([pattern, where].filter(Boolean).join(' · '), 52);
                const base = toolName === TOOLS.GREP ? 'search' : 'find';
                const loadingLabel = detail
                    ? `${base === 'search' ? 'Searching' : 'Finding'}: ${detail}`
                    : (base === 'search' ? 'Searching files' : 'Finding files');
                const successLabel = detail
                    ? `${base === 'search' ? 'Searched' : 'Found'}: ${detail}`
                    : (base === 'search' ? 'Search complete' : 'Files found');
                const failureLabel = detail
                    ? `${base === 'search' ? 'Search failed' : 'Find failed'}: ${detail}`
                    : (base === 'search' ? 'Search failed' : 'Find failed');

                return {
                    icon: 'search',
                    name: status === 'loading' ? loadingLabel : (status === 'success' ? successLabel : failureLabel)
                };
            }

        case TOOLS.DEEP_THINK:
            return {
                icon: 'search',
                name: 'Deep Thinking'
            };

        case TOOLS.SEARCH_TOOLS:
        case TOOLS.COMPOSIO_SEARCH_TOOLS:
            return {
                icon: 'search',
                name: 'Search Tools'
            };

        case TOOLS.IMAGE_ANALYSIS:
            return {
                icon: 'image-plus',
                name: status === 'loading' ? 'Analyzing image' : (status === 'success' ? 'Image analyzed' : 'Image analysis failed')
            };
        case TOOLS.KNOWLEDGE_BASE:
            return {
                icon: 'database',
                name: status === 'loading' ? 'Searching knowledge base' : (status === 'success' ? 'Knowledge base searched' : 'Knowledge base search failed')
            };
        case TOOLS.PATS_LOOKUP:
            return {
                icon: 'search',
                name: status === 'loading' ? 'Looking up PATs' : (status === 'success' ? 'PATs lookup completed' : 'PATs lookup failed')
            };
        case TOOLS.BROAD_SEARCH:
            return {
                icon: 'binoculars',
                name: status === 'loading' ? 'Broad search in progress' : (status === 'success' ? 'Broad search completed' : 'Broad search failed')
            };
        case TOOLS.DEEP_SEARCH:
            return {
                icon: 'search',
                name: status === 'loading' ? 'Deep search in progress' : (status === 'success' ? 'Deep search completed' : 'Deep search failed')
            };
        case TOOLS.VIEW_IMAGE:
            return {
                icon: 'image',
                name: status === 'loading' ? 'Loading image' : (status === 'success' ? 'View image' : 'View image failed')
            };
        // Fallback for COMPOSIO tools and unknown tools
        default:
            {
                let displayName = toolName;

                // Handle COMPOSIO prefixed tools
                if (toolName.startsWith('COMPOSIO_')) {
                    displayName = toolName.replace('COMPOSIO_', '');
                }

                // Check for toolkit provider match
                const normalizedToolName = displayName.toLowerCase();
                const matchedProvider = Object.values(TOOLKIT_PROVIDERS).find(
                    provider => normalizedToolName.includes(provider.toLowerCase())
                );

                if (matchedProvider) {
                    return {
                        iconSource: IconSourceEnum.SVG,
                        icon: CONNECTOR_PROVIDER_INFO[matchedProvider as CONNECTOR_PROVIDERS].icon,
                        name: displayName
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                    };
                }

                // Check for SQL-related tools
                if (normalizedToolName.includes('sql')) {
                    return {
                        icon: 'database',
                        name: displayName
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                    };
                }

                // Generic fallback
                return {
                    icon: 'wrench',
                    name: displayName
                        .split(/[-_]/)
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')
                };
            }
    }
}

export function copyCanvasContent(canvasComponents: QueryList<CanvasComponent>, message: MessageItem) {
    if (!canvasComponents) {
        return false;
    }
    // Find the canvas component that matches this message
    const canvasComponent = canvasComponents.find(canvas => canvas.message()!.id === message.id);
    if (!canvasComponent) {
        return false;
    }

    // Get the rendered HTML from the matching canvas component
    const htmlContent = canvasComponent.getRenderedHTML();

    // Create a ClipboardItem with both HTML and plain text formats
    // This allows applications to choose the format they support
    const canvasPlainContent = message['additional_kwargs']?.canvasContent;
    const plainText = canvasPlainContent;
    const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' })
    });

    navigator.clipboard.write([clipboardItem]).then(() => {
        return true;
    }).catch(err => {
        console.error('Failed to copy message:', err);
        return false;
    });
    return true;
}
